/*
    Implements a messages display box and chatting UI features
 */

//messages formatting types
//if using together with UIServiceMessenger this constants will be redefined with appropriate SM_TYPE_ constants (see UIServiceMessenger)
//if using together with mylocator this constants will be redefined with appropriate CM_TYPE_ constants
//mylocator legacy support functionality need to be removed after mylocator refactoring

import {elog} from "@/engine/helpers/logging.js";
import {UICombobox} from "@/engine/UIcomponents/UICombobox.js";

export const M_FORMAT_INAPP_NOTICE      = 1;  //inapp system notification
export const M_FORMAT_CLIENT_NOTICE     = 2;  //notification where sender and recipient is defined
export const M_FORMAT_CHAT_MESSAGE      = 3;  //chat message where sender and recipient is defined

/*
container - DOM Element UIMessages will be inserted to
template - HTML-template for messages UI
options - options object
options.storage - storage functions object
options.unreadChangedCallback - calls when unread counter is changing
    options.messagesClickCallback - calls on clicks on messages. target must have css classname 'clickable' - doesn't used now
options.sendMessageCallback - calls on send message button click, callback must return true on success and input box will be cleared
options.ownerUID - owner uid (optional, need to replace owner's name to 'You'

storage.getFrom(n)  - function to get last n messages from a storage
storage.getNew(Id) - function to get new messages after Id
 */
export class UIMessages {
    constructor(container, template, options) {
        this._container = container;
        if (typeof template === 'string') {
            this._container.innerHTML = template;
        } else {
            this._container.appendChild(template);
        }

        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };

        //hide display by default
        this.hide();

        this.lastReadedIdx = 0;
        this.unreadCounter = 0;
        this.unreadChangedCallback = null;
        this.sendMessageCallback = null;
        this.ownerUID = null;
        this.recipientUID = null;
        this.recipients = new Map();
        this.lastMessage = null;

        if (options) {
            this.storage = options.storage;
            this.unreadChangedCallback = options.unreadChangedCallback;
            this.sendMessageCallback = options.sendMessageCallback;
            this.ownerUID = options.ownerUID;
        }

        this.M_FORMAT_INAPP_NOTICE = M_FORMAT_INAPP_NOTICE
        this.M_FORMAT_CLIENT_NOTICE = M_FORMAT_CLIENT_NOTICE
        this.M_FORMAT_CHAT_MESSAGE = M_FORMAT_CHAT_MESSAGE

        if (typeof GeoContext == "function") {
            //backward compatibility with legacy integration to mylocator app
            this.M_FORMAT_INAPP_NOTICE = CM_TYPE_INAPP_NOTICE
            this.M_FORMAT_CLIENT_NOTICE = CM_TYPE_CLIENT_NOTICE
            this.M_FORMAT_CHAT_MESSAGE = CM_TYPE_CHAT_MESSAGE
        } else if (typeof SM_TYPE_CLIENT_NOTICE == "number") {
            //compatibility with integration to UIServiceMessenger
            this.M_FORMAT_INAPP_NOTICE = SM_TYPE_INAPP_NOTICE
            this.M_FORMAT_CLIENT_NOTICE = SM_TYPE_CLIENT_NOTICE
            this.M_FORMAT_CHAT_MESSAGE = SM_TYPE_CHAT_MESSAGE
        }

        //disable window events propagation
        L.DomEvent.disableScrollPropagation(this._container);
        L.DomEvent.disableClickPropagation(this._container);
        L.DomEvent.on(this._container, 'contextmenu', L.DomEvent.stopPropagation);

        //messages container
        this.mContainer = this._container.querySelector(".messageList");
        //input container
        this.iContainer = this._container.querySelector(".inputPane");

        //handle clicks on messages to process inmessages hyperlinks
        this.mContainer.addEventListener("click", function (ev) {
            if (ev.target.classList.contains("clickable")) {
                //todo obsolete, legacy mylocator code
                let geoObject = geoContext.getGeoObject(ev.target.getAttribute("data-bind"));
                if (geoObject)
                    geoContext.mapViewToGeoObject(geoObject);
            }
        }.bind(this));

        this.recipientBox = new UICombobox(this._container.querySelector(".recipient"), [], {id: "chatRecipient", changeCallback: function (v) {
                this.setRecipient(v);
            }.bind(this)}
        );

        //handle input box enter key
        this.iContainer.querySelector('input[type="text"]').addEventListener('keyup', function (e) {
            if (e.keyCode === 13) {
                this.sendCurrentTypingMessage()
            }
        }.bind(this));
        //handle clicks on send button
        this.iContainer.querySelector('.sendButton').addEventListener('click', this.sendCurrentTypingMessage.bind(this));

        //messages time and dates formatters
        this.timeFormatter = new Intl.DateTimeFormat('ru-RU', {
            hour: '2-digit', minute: '2-digit'
        });
        this.dateFormatter = new Intl.DateTimeFormat('en-GB', {
            day: 'numeric', month: 'short'
        });

        //load first 100 messages in a date descent order
        this._loadBackFrom(new Date().getTime(), 100).then(function (messages) {
            let el;
            if (this.lastMessage != null) {
                // don't add loaded messages if there are messages already on a display
                // These are most likely very important messages added at an initiating stage
                return
            }
            this.active = true; //a crunch, prevent a badge appearing during initial messages load
            while((el = messages.pop())) {
                this.AddMessage(el);
            }
            this.active = false;
        }.bind(this));
    }

    /*
    push message into message List
    message - message object
    message.timestamp
    message.uid
    message.type
    message.payload || message.data (legacy)
     */
    AddMessage(message) {
        //this.messages.push(message);
        if (message.payload.suppress_the_same &&
            this.lastMessage != null &&
            message.payload.text == this.lastMessage.payload.text) {
            return;
        }
        let el = this._formatMessage(message);
        if (el) {
            this.lastMessage = message;
            if (message.payload.attention) {
                this.show();
            }
            this.mContainer.append(el);
            if (this.active) {
                //scroll window down
                this.mContainer.scrollTop = this.mContainer.scrollHeight;
                this.lastReadedIdx = message.timestamp;
                this._unreadCounter(0)
            } else {
                this._unreadCounter(this._unreadCounter() + 1);
            }
        }
    }

    //replace message with uid
    ReplaceMessage(uid, message) {
        let oldEl = this.mContainer.querySelector(`[data-uid="${uid}"]`);
        if (! oldEl)
            return false;
        let newEl = this._formatMessage(message);
        oldEl.parentNode.replaceChild(newEl, oldEl);
        return true;
    }
    /*
        create html element for message according a message.type
     */
    _formatMessage(message) {
        let el = document.createElement('p');
        el.setAttribute("for", message.timestamp);
        el.setAttribute("data-uid", message.uid);
        if (message.payload.color) {
            el.style.color = message.payload.color;
        }
        /* set animation for new messages (no older 10s) */
        if ((new Date().getTime() - message.timestamp) < 10000) {
            el.classList.add("blink");
            /* remove an animation class when animation ends, in order to prevent an animation restart after window hide/show */
            el.addEventListener(
                "animationend",
                function(obj) { obj.target.classList.remove("blink") },
                {once: true}
            );
        }

        let mDate = this.dateFormatter.format(message.timestamp);
        let mTime = this.timeFormatter.format(message.timestamp);
        let mHead = `<span class="timestamp">${mDate} ${mTime}</span>:`;

        let mBody = "";
        switch (message.type) {
            case this.M_FORMAT_INAPP_NOTICE: {
                mBody = message.payload.text;
                if (message.payload.object) {
                    mBody = this._decorateObject(mBody, message.payload.object);
                }
                if (message.payload.url)
                    mBody += ` <a href="${message.payload.url}">${message.payload.url}</a>`;
                break;
            }
            case this.M_FORMAT_CLIENT_NOTICE: {
                mBody = this._decorateObject(message.payload.message, [message.payload.from, message.payload.to]);
                break;
            }
            case this.M_FORMAT_CHAT_MESSAGE: {
                let data = message.payload;
                if (data) {
                    let mFrom  = this._decorateObject(null, data.from.uid !== this.ownerUID ? data.from : { name: "You", uid: data.from.uid});
                    let mTo  = this._decorateObject(null, data.to.uid !== this.ownerUID ? data.to : { name: "You", uid: data.to.uid});
                    mBody = `${mFrom} to ${mTo}: ${data.message}`;
                }
                break;
            }
            default: {
                this.elog('WARN', `Message of type ${message.type} isn't supported and can't be displayed`)
            }
        }
        if (mBody && mBody !== "") {
            el.innerHTML = mHead + ' ' + `<span class="message">${mBody}</span>`;
            return el;
        }

        return null;
    }

    /*
        replace names in text with decorators (clickable links)
        return text where all object names is replaced with decorators
        text -  an original text to search in. If a text isn't empty, all names will be replaced with decorators
                if text is empty, just first object decorator will be returned
        object - can be a single object or array of objects
            [object.name] - name we search in the text
            [object.uid | object.gouid (legacy)] - uid we place into a data-bind attribute to subsequent click processing
     */
    _decorateObject(text, object) {
        let objects = (object instanceof Array) ? object : [object];

        function decorator(object) {
            if (object == null)
                return null;

            let uid = object.gouid || object.uid || object.uuid;
            let name = object.name;

            if (uid == null || name == null)
                return null;

            return [uid, name, `<span class="clickable" data-bind="${uid}" data-type="object">${name}</span>`];
        }

        if (text == null || text === "")
            return decorator(objects[0])[2];

        objects.forEach(function (el) {
            let decor = decorator(el);
            //replace uids
            if (decor != null) {
                let newText = text.replace( '{'+decor[0]+'}', decor[2] );
                if (text !== newText) {
                    text = newText
                } else {
                    //if there are no uids, try to replace names
                    text = text.replace( decor[1], decor[2] )
                }
            }
        });
        return text;
    }

    /*
     * set current recipient
     */
    setRecipient(uid) {
        if (this.recipientUID !== uid) {
            this.recipientUID = uid;
            this._updateRecipient();
        }
    }

    /*
     * initialise recipient list
     * array of objects
     * object:
     *    name
     *    uid
     */
    setRecipients(list) {
        this.recipients.clear();

        list.forEach( function (el) {
            this.recipients.set(el.uid, el.name);
        }.bind(this));

        this._updateRecipientSelectContainer();
        this._updateRecipient();
    }

    deleteRecipient(uid) {
        this.recipients.delete(uid);
        this._updateRecipientSelectContainer();
        this._updateRecipient();
    }

    _sortedRecipients() {
        let map = this.recipients;

       return [...map.keys()].sort(function (a, b) {
            return (map.get(a) > map.get(b)) ? 1 : ((map.get(a) < map.get(b)) ? -1 : 0);
        }).map(function (item) {
            return {name: map.get(item), value: item}
        });
    }

    _updateRecipientSelectContainer() {
        //remove all option elements from select
        this.recipientBox.removeAll();
        for (let recipient of this._sortedRecipients()) {
            this.recipientBox.addElement(recipient.name, recipient.value)
        }
    }

    _updateRecipient() {
        if (this.recipientUID) {
            let recipient = this.recipients.get(this.recipientUID);
            if (! recipient && this.recipients.size > 0) {
                this.recipientUID = this.recipients.keys().next().value;
                this._updateRecipient();
                return;
            }
            if (recipient) {
                //we must have sendMessageCallback
                if (this.sendMessageCallback) {
                    this.iContainer.style.display = "";
                    this.recipientBox.select(this.recipientUID);
                    return;

                } else {
                    console.warn("sendMessagesCallback isn't defined for UIMessenger");
                }
            } else {
                this.recipientUID = null;
            }
        }
        this.iContainer.style.display = "none";
    }

    async _loadBackFrom(idx, n) {
        if (!this.storage)
            return [];

        try {
            return await this.storage.getFrom(idx, n);
        } catch (e) {
            console.log("Storage error: " + e.message);
        }
        return [];
    }

    _unreadCounter(n) {
        if (n == null)
            return this.unreadCounter;
        if (n !== this.unreadCounter) {
            this.unreadCounter = n;
            if (this.unreadChangedCallback) {
                this.unreadChangedCallback(this.unreadCounter);
            }
        }
    }

    show() {
        this._container.classList.remove('collapsed');
        this._container.classList.add('expanded');
        this.active = true;
        //scroll down
        this.mContainer.scrollTop = this.mContainer.scrollHeight;

        this._unreadCounter(0);

        let lastMessage = this.mContainer.lastElementChild;
        if (lastMessage)
            this.lastReadedIdx = this.mContainer.lastElementChild.getAttribute("for");

        this._updateRecipient();
    }

    hide() {
        this._container.classList.remove('expanded');
        this._container.classList.add('collapsed');
        this.active = false;
    }

    isActive() {
        return this.active;
    }

    getCurrentTypingMessage() {
        return this.iContainer.querySelector('input[type="text"]').value;
    }

    setCurrentTypingMessage(m) {
        this.iContainer.querySelector('input[type="text"]').value = m;
    }

    sendCurrentTypingMessage() {
        if (this.sendMessageCallback && this.recipientUID) {
            if (this.sendMessageCallback(this.recipientUID, this.getCurrentTypingMessage())) {
                this.setCurrentTypingMessage("");
            }
        }
    }

    badge(container, num) {
        var badgeChild = container.firstElementChild;
        if(badgeChild.className==='badge-num')
            container.removeChild(badgeChild);

        if (num > 0) {
            let badgeNum = document.createElement('div');
            badgeNum.setAttribute('class','badge-num');
            badgeNum.innerText = num;
            container.insertBefore(badgeNum, container.firstChild);
        }
    }
}
